'use client';

import { useEffect, useRef, useState } from 'react';

export type ScrollDirection = 'up' | 'down';

interface UseScrollDirectionOptions {
  /** 이 임계치(px) 이하로 스크롤 됐을 때는 항상 'up' 으로 본다(상단 영역 보호). */
  topThreshold?: number;
  /** 이 양(px) 이상 변했을 때만 방향 갱신. 작은 떨림으로 토글되는 것 방지. */
  delta?: number;
}

/**
 * 페이지 스크롤 방향(up/down) 추적. SSR 안전하게 mount 이후만 동작.
 * 쿠폰 적용 상품 페이지의 sort 영역이 역스크롤 시에만 sticky 가 되도록 사용한다.
 */
/**
 * 한 방향으로 누적된 스크롤이 `delta` 이상일 때만 direction 을 전환한다 (hysteresis).
 * 추가로 방향 전환 직후 `cooldownMs` 동안은 재전환을 막아 momentum scroll·작은 떨림으로
 * direction 이 빠르게 토글되며 sticky sort 가 깜박이는 현상을 방지한다.
 */
const COOLDOWN_MS = 250;

export const useScrollDirection = ({
  topThreshold = 4,
  delta = 24,
}: UseScrollDirectionOptions = {}): ScrollDirection => {
  const [direction, setDirection] = useState<ScrollDirection>('up');
  const lastY = useRef(0);
  const accumulated = useRef(0);
  const lastTransitionAt = useRef(0);

  useEffect(() => {
    lastY.current = window.scrollY;
    const handler = () => {
      const currentY = window.scrollY;
      if (currentY <= topThreshold) {
        setDirection('up');
        accumulated.current = 0;
        lastY.current = currentY;
        return;
      }
      const diff = currentY - lastY.current;
      lastY.current = currentY;
      // 직전 전환 후 cooldown 안에서는 방향 평가 자체를 건너뛴다.
      if (performance.now() - lastTransitionAt.current < COOLDOWN_MS) {
        accumulated.current = 0;
        return;
      }
      // 방향이 바뀌면 누적치 reset → 한 방향으로 delta 이상 움직였을 때만 전환.
      if ((accumulated.current > 0 && diff < 0) || (accumulated.current < 0 && diff > 0)) {
        accumulated.current = diff;
      } else {
        accumulated.current += diff;
      }
      if (accumulated.current >= delta) {
        setDirection('down');
        accumulated.current = 0;
        lastTransitionAt.current = performance.now();
      } else if (accumulated.current <= -delta) {
        setDirection('up');
        accumulated.current = 0;
        lastTransitionAt.current = performance.now();
      }
    };
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, [topThreshold, delta]);

  return direction;
};
